import React, { useState, useRef, useEffect } from 'react';
import TopNavbar from '../../../components/TopNavbar';
import SideNavbar from '../../../components/SideNavbar';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy, startAfter, limit, Timestamp, QueryConstraint, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../../firebaseConfig';
import { useAuth } from '../../../AuthContext';
import * as XLSX from 'xlsx';
import Toast from '../../../components/Toast';

// redux
import { useAppSelector } from '../../../redux/store';
import getCurrentScreen from '../../../Helper/GetScreen';

interface Student {
    id: string;
    name: string;
    createdAt: Timestamp;
    source: { source: string };
    stage: { color: string; priority: number; stage: string };
    priority: { color: string; priority: number; status: string };
    admissionStatus?: { admissionStatus: string };
}

interface UserData {
    companyId: string;
    isAdmin: boolean;
    name: string;
}

interface FilterOption {
    id: string;
    name?: string;
    source?: string;
    stage?: string;
    status?: string;
    color?: string;
    admissionStatus?: string;
}

const AllStudents = () => {

    const { currentUser, isAdmin, isManager, isStaff, userData } = useAuth();
    const currentScreen = getCurrentScreen(useAppSelector(state => state.screen.currentScreen), isAdmin, isManager, isStaff);
    const isCollapsed = useAppSelector(state => state.sidebar.isCollapsed);

    const [students, setStudents] = useState<Student[]>([]);
    const [loading, setLoading] = useState(false);
    const [lastVisible, setLastVisible] = useState<any>(null);
    const [hasMore, setHasMore] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [filters, setFilters] = useState({
        source: [] as string[],
        stage: [] as string[],
        priority: [] as string[],
        admissionStatus: [] as string[]
    });
    const [filterOptions, setFilterOptions] = useState({
        sources: [] as FilterOption[],
        stages: [] as FilterOption[],
        priorities: [] as FilterOption[],
        admissionStatuses: [] as FilterOption[]
    });
    // const [isAdmin, setIsAdmin] = useState(false);
    // const [userData, setUserData] = useState<UserData | null>(null);
    const [showCollaborateModal, setShowCollaborateModal] = useState(false);
    const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
    const [selectedStudentPhone, setSelectedStudentPhone] = useState<string>('');
    const [collaborators, setCollaborators] = useState<string[]>([]);
    const [selectedUserDetails, setSelectedUserDetails] = useState<{ id: string, name: string }[]>([]);
    const [searchResults, setSearchResults] = useState<{ id: string, name: string }[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [isErrorToast, setIsErrorToast] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [selectedTransferUser, setSelectedTransferUser] = useState<{ id: string, name: string } | null>(null);
    const [currentAssignedUser, setCurrentAssignedUser] = useState<{ id: string, name: string } | null>(null);
    const filterPopupRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const PAGE_SIZE = 10;

    // Fetch filter options
    useEffect(() => {
        const fetchFilterOptions = async () => {
            try {
                const [sourcesSnap, stagesSnap, prioritiesSnap, admissionStatusesSnap] = await Promise.all([
                    getDocs(collection(db, `companies/${userData?.companyId}/leadSources`)),
                    getDocs(collection(db, `companies/${userData?.companyId}/leadStages`)),
                    getDocs(collection(db, `companies/${userData?.companyId}/leadStatuses`)),
                    getDocs(collection(db, `companies/${userData?.companyId}/leadAdmissionStatus`))
                ]);

                setFilterOptions({
                    sources: sourcesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })),
                    stages: stagesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })),
                    priorities: prioritiesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })),
                    admissionStatuses: admissionStatusesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }))
                });
            } catch (error) {
                console.error('Error fetching filter options:', error);
            }
        };

        fetchFilterOptions();
    }, []);

    // // Get user data including company ID
    // useEffect(() => {
    //     const getUserData = async () => {
    //         if (!currentUser) return;
    //         try {
    //             const userDocRef = doc(db, 'users', currentUser.uid);
    //             const userDocSnap = await getDoc(userDocRef);
    //             if (userDocSnap.exists()) {
    //                 const data = userDocSnap.data() as UserData;
    //                 // setUserData(data);
    //                 // setIsAdmin(data.isAdmin);
    //             }
    //         } catch (error) {
    //             console.error('Error fetching user data:', error);
    //         }
    //     };
    //     getUserData();
    // }, [currentUser]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (filterPopupRef.current && !filterPopupRef.current.contains(event.target as Node)) {
                setIsFilterOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const fetchStudents = async (isNewQuery: boolean = true) => {
        if (!currentUser || !userData || loading) return;

        // console.log('Fetching students...');
        // console.log("currentScreen", currentScreen);
        // console.log("isAdmin", isAdmin);
        // console.log("isManager", isManager);
        // console.log("isStaff", isStaff);




        try {
            setLoading(true);
            const studentsRef = collection(db, `companies/${userData.companyId}/leads`);

            // Base query conditions
            const conditions: QueryConstraint[] = [
                where('isActive', '==', true),
                orderBy('createdAt', 'desc'),
                orderBy('name', 'asc'),
                limit(PAGE_SIZE)
            ];

            if (isAdmin && currentScreen === 'Admin') {
                // Add user-specific condition for admin users
                console.log('Admin User');
            }
            // Add user-specific condition for non-admin users
            else if (isManager && currentScreen === 'Manager') {
                console.log('Manager User');
                const userQuery = query(
                    collection(db, 'users'),
                    where('companyId', '==', userData.companyId),
                    where('managerId', '==', currentUser?.uid)
                );

                const userQuerySnapshot = await getDocs(userQuery);
                const usersData = userQuerySnapshot.docs.map(doc => doc.id);

                // Add user-specific condition for non-admin users
                conditions.push(where('assignedTo', 'in', usersData));
            } else if (isStaff && currentScreen === 'Staff') {
                console.log('Staff User');
                conditions.push(where('assignedTo', '==', currentUser.uid));
            }

            // Add search condition if search query exists
            if (searchQuery.trim()) {
                const upperCaseQuery = searchQuery.trim().toUpperCase();
                conditions.push(where('name', '>=', upperCaseQuery));
                conditions.push(where('name', '<=', upperCaseQuery + '\uf8ff'));
            }

            // Add filters if selected
            if (filters.source.length > 0) {
                conditions.push(where('sourceId', 'in', filters.source));
            }
            if (filters.stage.length > 0) {
                conditions.push(where('stageId', 'in', filters.stage));
            }
            if (filters.priority.length > 0) {
                conditions.push(where('statusId', 'in', filters.priority));
            }
            if (filters.admissionStatus.length > 0) {
                conditions.push(where('admissionStatusId', 'in', filters.admissionStatus));
            }


            // Add startAfter condition for pagination
            if (!isNewQuery && lastVisible) {
                conditions.push(startAfter(lastVisible));
            }

            const studentsQuery = query(studentsRef, ...conditions);
            const snapshot = await getDocs(studentsQuery);

            // Update lastVisible for pagination
            setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
            setHasMore(snapshot.docs.length === PAGE_SIZE);

            const studentsData = await Promise.all(snapshot.docs.map(async (doc) => {
                const data = doc.data();
                const sourceDoc = data.sourceId ? await getDocs(collection(db, `companies/${userData?.companyId}/leadSources`)).then(
                    snap => snap.docs.find(d => d.id === data.sourceId)?.data()
                ) : null;
                const stageDoc = data.stageId ? await getDocs(collection(db, `companies/${userData?.companyId}/leadStages`)).then(
                    snap => snap.docs.find(d => d.id === data.stageId)?.data()
                ) : null;
                const priorityDoc = data.statusId ? await getDocs(collection(db, `companies/${userData?.companyId}/leadStatuses`)).then(
                    snap => snap.docs.find(d => d.id === data.statusId)?.data()
                ) : null;
                const admissionStatusDoc = data.admissionStatusId ? await getDocs(collection(db, `companies/${userData?.companyId}/leadAdmissionStatus`)).then(
                    snap => snap.docs.find(d => d.id === data.admissionStatusId)?.data()
                ) : null;

                return {
                    id: doc.id,
                    name: data.name,
                    createdAt: data.createdAt,
                    source: {
                        source: sourceDoc?.source || 'Not Specified'
                    },
                    stage: {
                        stage: stageDoc?.stage || 'N/A',
                        color: stageDoc?.color || '#808080',
                        priority: stageDoc?.priority || 0
                    },
                    priority: {
                        status: priorityDoc?.status || 'N/A',
                        color: priorityDoc?.color || '#808080',
                        priority: priorityDoc?.priority || 0
                    },
                    admissionStatus: admissionStatusDoc ? {
                        admissionStatus: admissionStatusDoc.admissionStatus
                    } : undefined
                } as Student;
            }));

            if (isNewQuery) {
                setStudents(studentsData);
            } else {
                setStudents(prev => [...prev, ...studentsData]);
            }
        } catch (error) {
            console.error('Error fetching students:', error);
        } finally {
            setLoading(false);
        }
    };

    // Initial fetch when user data is loaded
    useEffect(() => {
        if (userData) {
            fetchStudents();
        }
    }, [userData, currentScreen]);

    // excel download
    const exportStudentsToExcel = async () => {
        if (!currentUser || !userData || loading) return;

        try {
            setLoading(true);
            const studentsRef = collection(db, `companies/${userData.companyId}/leads`);

            // Base query conditions - removing pagination limit for full data
            let conditions: QueryConstraint[] = [
                where('isActive', '==', true),
                orderBy('createdAt', 'desc'),
                orderBy('name', 'asc')
            ];

            if (isAdmin && currentScreen === 'Admin') {
                // Add user-specific condition for admin users
                console.log('Admin User');
            }
            // Add user-specific condition for non-admin users
            else if (isManager && currentScreen === 'Manager') {
                const userQuery = query(
                    collection(db, 'users'),
                    where('companyId', '==', userData.companyId),
                    where('managerId', '==', currentUser?.uid)
                );

                const userQuerySnapshot = await getDocs(userQuery);
                const usersData = userQuerySnapshot.docs.map(doc => doc.id);

                // Add user-specific condition for non-admin users
                conditions.push(where('assignedTo', 'in', usersData));
            } else if (isStaff && currentScreen === 'Staff') {
                conditions.push(where('assignedTo', '==', currentUser.uid));
            }

            // Add search condition if search query exists
            if (searchQuery.trim()) {
                const upperCaseQuery = searchQuery.trim().toUpperCase();
                conditions.push(where('name', '>=', upperCaseQuery));
                conditions.push(where('name', '<=', upperCaseQuery + '\uf8ff'));
            }

            // Add filters if selected and non-empty
            if (filters.source.length > 0) {
                conditions.push(where('sourceId', 'in', filters.source));
            }
            if (filters.stage.length > 0) {
                conditions.push(where('stageId', 'in', filters.stage));
            }
            if (filters.priority.length > 0) {
                conditions.push(where('statusId', 'in', filters.priority));
            }
            if (filters.admissionStatus.length > 0) {
                conditions.push(where('admissionStatusId', 'in', filters.admissionStatus));
            }

            const studentsQuery = query(studentsRef, ...conditions);
            const snapshot = await getDocs(studentsQuery);

            const studentsData = await Promise.all(snapshot.docs.map(async (doc) => {
                const data = doc.data();

                // Fetch source, stage, status, and state data
                const sourceDoc = data.sourceId ? await getDocs(collection(db, `companies/${userData?.companyId}/leadSources`)).then(
                    snap => snap.docs.find(d => d.id === data.sourceId)?.data()
                ) : null;
                const stageDoc = data.stageId ? await getDocs(collection(db, `companies/${userData?.companyId}/leadStages`)).then(
                    snap => snap.docs.find(d => d.id === data.stageId)?.data()
                ) : null;
                const priorityDoc = data.statusId ? await getDocs(collection(db, `companies/${userData?.companyId}/leadStatuses`)).then(
                    snap => snap.docs.find(d => d.id === data.statusId)?.data()
                ) : null;
                const stateDoc = data.stateId ? await getDocs(collection(db, 'leadStates')).then(
                    snap => snap.docs.find(d => d.id === data.stateId)?.data()
                ) : null;
                const admissionStatusDoc = data.admissionStatusId ? await getDocs(collection(db, `companies/${userData?.companyId}/leadAdmissionStatus`)).then(
                    snap => snap.docs.find(d => d.id === data.admissionStatusId)?.data()
                ) : null;

                // Fetch assigned user and updated by user emails with company filter
                const assignedToDoc = data.assignedTo ? await getDocs(
                    query(
                        collection(db, 'users'),
                        where('companyId', '==', userData.companyId)
                    )
                ).then(
                    snap => snap.docs.find(d => d.id === data.assignedTo)?.data()
                ) : null;

                const updatedByDoc = data.updatedBy ? await getDocs(
                    query(
                        collection(db, 'users'),
                        where('companyId', '==', userData.companyId)
                    )
                ).then(
                    snap => snap.docs.find(d => d.id === data.updatedBy)?.data()
                ) : null;

                // Fetch follow-up for this student
                const followUpQuery = query(
                    collection(db, `companies/${userData.companyId}/followUps`),
                    where('leadId', '==', doc.id),
                    where('isDone', '==', false),
                    orderBy('dueDate', 'asc'),
                    limit(1)
                );
                const followUpSnapshot = await getDocs(followUpQuery);
                const followUp = followUpSnapshot.docs[0]?.data();

                return {
                    name: data.name || '',
                    email: data.email || '',
                    number: data.number || '',
                    createdAt: data.createdAt?.toDate().toLocaleDateString() || '',
                    updatedAt: data.updatedAt?.toDate().toLocaleDateString() || '',
                    adminNotes: data.adminNotes || '',
                    destinationCountry: data.destinationCountry || '',
                    destinationCountryCode: data.destinationCountryCode || '',
                    intakeMonth: data.intakeMonth || '',
                    intakeYear: data.intakeYear || '',
                    // isActive: data.isActive?.toString() || '',
                    preferredStream: data.preferredStream || '',
                    programType: data.programType || '',
                    stage: stageDoc?.stage || '',
                    state: stateDoc?.state || '',
                    status: priorityDoc?.status || '',
                    assignedTo: assignedToDoc?.email || '',
                    updatedBy: updatedByDoc?.email || '',
                    Source: sourceDoc?.source || '-',
                    AdmissionStatus: admissionStatusDoc?.admissionStatus || 'Not Set',
                    Followup: followUp?.notes || '',
                    'Followup Date': followUp?.dueDate?.toDate().toLocaleDateString() || ''
                };
            }));

            console.log('Filtered Students Data:', studentsData);
            console.log('Total Records:', studentsData.length);
            setToastMessage('Data downloaded successfully!');
            setIsErrorToast(false);
            setShowToast(true);

            // Create worksheet
            const ws = XLSX.utils.json_to_sheet(studentsData);

            // Create workbook and add worksheet
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Students');

            // Generate Excel file
            const fileName = `students_export_${new Date().toISOString().split('T')[0]}.xlsx`;
            XLSX.writeFile(wb, fileName);

        } catch (error) {
            setToastMessage('Failed to download data. Please try again.');
            setIsErrorToast(true);
            setShowToast(true);
            console.error('Error exporting students:', error);
        } finally {
            setLoading(false);
        }
    };

    // Handle search
    const handleSearch = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            setStudents([]);
            setLastVisible(null);
            setHasMore(true);
            fetchStudents();
        }
    };

    // Handle filter changes
    const handleFilterChange = (type: 'source' | 'stage' | 'priority' | 'admissionStatus', value: string) => {
        setFilters(prev => ({
            ...prev,
            [type]: prev[type].includes(value)
                ? prev[type].filter(item => item !== value)
                : [...prev[type], value]
        }));
    };

    // Apply filters
    const applyFilters = () => {
        setStudents([]);
        setLastVisible(null);
        setHasMore(true);
        fetchStudents();
        setIsFilterOpen(false);
    };

    // Load more
    const loadMore = () => {
        if (hasMore && !loading) {
            fetchStudents(false);
        }
    };

    const handleStudentClick = (studentId: string) => {
        navigate(`/studentdetails/${studentId}`);
    };

    const handleCollaborateClick = async (student: Student) => {
        // Clear previous state
        setSelectedUserDetails([]);
        setCollaborators([]);
        setSearchTerm('');
        setSearchResults([]);

        setSelectedStudent(student);
        if (userData?.companyId) {
            try {
                const leadRef = doc(db, `companies/${userData.companyId}/leads`, student.id);
                const leadDoc = await getDoc(leadRef);
                if (leadDoc.exists()) {
                    const leadData = leadDoc.data();
                    setSelectedStudentPhone(leadData.number || 'No number available');
                }
                // Fetch existing collaborators
                await fetchExistingCollaborators(student);
            } catch (error) {
                console.error('Error fetching student data:', error);
                setSelectedStudentPhone('No number available');
            }
        }
        setShowCollaborateModal(true);
    };

    const handleTransferClick = async (student: Student) => {
        setSelectedStudent(student);
        setSelectedTransferUser(null);
        setSearchTerm('');
        setSearchResults([]);
        setCurrentAssignedUser(null);

        if (userData?.companyId) {
            try {
                const leadRef = doc(db, `companies/${userData.companyId}/leads`, student.id);
                const leadDoc = await getDoc(leadRef);
                if (leadDoc.exists()) {
                    const leadData = leadDoc.data();
                    setSelectedStudentPhone(leadData.number || 'No number available');

                    // Fetch current assigned user's name
                    if (leadData.assignedTo) {
                        const userDoc = await getDoc(doc(db, 'users', leadData.assignedTo));
                        if (userDoc.exists()) {
                            setCurrentAssignedUser({
                                id: userDoc.id,
                                name: userDoc.data().name
                            });
                        }
                    }
                }
            } catch (error) {
                console.error('Error fetching student data:', error);
                setSelectedStudentPhone('No number available');
            }
        }
        setShowTransferModal(true);
    };

    // Fetch available users with same companyId
    const fetchAvailableUsers = async () => {
        if (!userData?.companyId) return;

        try {
            const usersRef = collection(db, 'users');
            const q = query(usersRef, where('companyId', '==', userData.companyId));
            const querySnapshot = await getDocs(q);

            const usersData = querySnapshot.docs.map(doc => ({
                id: doc.id,
                name: doc.data().name
            }));

            setSearchResults(usersData);
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    };

    useEffect(() => {
        if (showCollaborateModal) {
            fetchAvailableUsers();
        }
    }, [showCollaborateModal]);

    const handleCollaborateSubmit = async () => {
        if (!selectedStudent || !userData?.companyId) return;

        try {
            const leadRef = doc(db, `companies/${userData.companyId}/leads`, selectedStudent.id);
            await updateDoc(leadRef, {
                collaborators: collaborators,
                collaborateDate: Timestamp.fromDate(new Date()), 
            });
            setToastMessage('Collaborators added successfully!');
            setIsErrorToast(false);
            setShowToast(true);
            setShowCollaborateModal(false);
            setCollaborators([]);
            setSelectedUserDetails([]);
            setSelectedStudent(null);
            setSearchTerm('');
            setSearchResults([]);
        } catch (error) {
            setToastMessage('Failed to add collaborators. Please try again.');
            setIsErrorToast(true);
            setShowToast(true);
            console.error('Error updating collaborators:', error);
        }
    };

    const handleTransferSubmit = async () => {
        if (!selectedStudent || !userData?.companyId || !selectedTransferUser) return;

        try {
            // Find the lead document of this specific student
            const leadRef = doc(db, `companies/${userData.companyId}/leads`, selectedStudent.id);

            // get the activeFollowUpId of this specific student
            const leadDoc = await getDoc(leadRef);
            const leadData = leadDoc?.data();
            const activeFollowUpId = leadData?.activeFollowUpId;



            // if activeFollowUpId is present, find the followUp document of this specific student
            const followUpRef = activeFollowUpId ? doc(db, `companies/${userData.companyId}/followUps`, activeFollowUpId) : null;


            // Update assignedTo field of this specific student
            await updateDoc(leadRef, {
                assignedTo: selectedTransferUser.id
            });

            // if activeFollowUpId is present, update userId field of this specific student's followUp document
            if (followUpRef) {
                await updateDoc(followUpRef, {
                    userId: selectedTransferUser.id
                });
            }

            // Update local state based on user type
            if (isAdmin && currentScreen === 'Admin') {
                // For admin, update the lead's assignedTo in the local state
                setStudents(prevStudents =>
                    prevStudents.map(student =>
                        student.id === selectedStudent.id
                            ? { ...student, assignedTo: selectedTransferUser.id }
                            : student
                    )
                );
            } else {
                // For normal users, remove the transferred lead from the local state
                setStudents(prevStudents =>
                    prevStudents.filter(student => student.id !== selectedStudent.id)
                );
            }

            setToastMessage('Lead transferred successfully!');
            setIsErrorToast(false);
            setShowToast(true);
            setShowTransferModal(false);
            setSelectedTransferUser(null);
            setSelectedStudent(null);
            setSearchTerm('');
            setSearchResults([]);
        } catch (error) {
            setToastMessage('Failed to transfer lead. Please try again.');
            setIsErrorToast(true);
            setShowToast(true);
            console.error('Error transferring lead:', error);
        }
    };

    const handleSearchUsers = async (value: string) => {
        if (!userData?.companyId || value.length < 3) {
            setSearchResults([]);
            return;
        }

        setIsLoadingUsers(true);
        try {
            const upperValue = value.toUpperCase();
            const usersRef = collection(db, 'users');
            const q = query(
                usersRef,
                where('companyId', '==', userData.companyId),
                where('name', '>=', upperValue),
                where('name', '<=', upperValue + '\uf8ff'),
                limit(10)
            );

            const querySnapshot = await getDocs(q);
            const usersData = querySnapshot.docs.map(doc => ({
                id: doc.id,
                name: doc.data().name
            }));

            setSearchResults(usersData);
        } catch (error) {
            console.error('Error searching users:', error);
        } finally {
            setIsLoadingUsers(false);
        }
    };

    const fetchExistingCollaborators = async (student: Student) => {
        if (!userData?.companyId) return;

        try {
            // First, get the lead document to get collaborator IDs
            const leadRef = doc(db, `companies/${userData.companyId}/leads`, student.id);
            const leadDoc = await getDoc(leadRef);
            if (leadDoc.exists()) {
                const leadData = leadDoc.data();
                const existingCollaborators = leadData.collaborators || [];
                setCollaborators(existingCollaborators);

                // Then, fetch user details for each collaborator
                if (existingCollaborators.length > 0) {
                    const userPromises = existingCollaborators.map(async (userId: string) => {
                        const userDoc = await getDoc(doc(db, 'users', userId));
                        if (userDoc.exists()) {
                            return {
                                id: userDoc.id,
                                name: userDoc.data().name
                            };
                        }
                        return null;
                    });

                    const users = await Promise.all(userPromises);
                    const validUsers = users.filter((user): user is { id: string, name: string } => user !== null);
                    setSelectedUserDetails(validUsers);
                }
            }
        } catch (error) {
            console.error('Error fetching existing collaborators:', error);
        }
    };

    const handleSelectUser = async (userId: string) => {
        if (!collaborators.includes(userId)) {
            // Add to collaborators array
            setCollaborators([...collaborators, userId]);

            // Add to selected user details
            const userDoc = await getDoc(doc(db, 'users', userId));
            if (userDoc.exists()) {
                const userData = userDoc.data();
                setSelectedUserDetails(prev => [...prev, {
                    id: userId,
                    name: userData.name
                }]);
            }
        }
    };

    const handleRemoveUser = (userId: string) => {
        setCollaborators(collaborators.filter(id => id !== userId));
        setSelectedUserDetails(prev => prev.filter(user => user.id !== userId));
    };

    const handleSelectTransferUser = (user: { id: string, name: string }) => {
        setSelectedTransferUser(user);
    };

    return (
        <div className="min-h-screen bg-gray-200">
            <div className="fixed top-16 left-0 h-full z-10">
                <SideNavbar />
            </div>

            <div className={`${isCollapsed ? 'ml-[70px]' : 'ml-[290px]'} mt-[109px] p-8 transition-all duration-300`}>
                <div className="bg-white p-6 rounded-3xl shadow-md">
                    <div className="flex items-center gap-6 mb-8 relative">
                        <div className="flex items-center justify-center w-14 h-14 rounded-full"
                            style={{ background: 'linear-gradient(180deg, #00A6D9 10%, #3483BE 100%)' }}>
                            <img src={`${process.env.PUBLIC_URL}/images/desktop/book-search-24.svg`} alt="Lead Icon" />
                        </div>
                        <h2 className="font-montserrat text-2xl font-medium text-black">All Students</h2>
                        <div className="ml-5 flex items-center w-80 rounded-3xl bg-zinc-100">
                            <img
                                src={`${process.env.PUBLIC_URL}/images/desktop/searchbg.svg`}
                                alt="search-lens-icon"
                                className="w-7 h-7 ml-2 cursor-pointer"
                                onClick={() => {
                                    setStudents([]);
                                    setLastVisible(null);
                                    setHasMore(true);
                                    fetchStudents();
                                }}
                            />
                            <input
                                type="text"
                                placeholder="Search for Student..."
                                className="w-full px-4 py-2 text-gray-700 bg-zinc-100 focus:outline-none rounded-3xl"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyPress={handleSearch}
                            />
                        </div>

                        <div className="ml-auto relative">
                            <div className="ml-auto flex items-center gap-4">
                                {isAdmin && currentScreen === "Admin" ? (
                                    <button
                                        className="px-4 py-2 text-white font-semibold rounded-full flex items-center gap-2"
                                        style={{ background: 'linear-gradient(180deg, #3483BE 0%, #02385E 100%)' }}
                                        onClick={exportStudentsToExcel}
                                        disabled={downloadLoading}
                                    >
                                        {downloadLoading ? 'Downloading...' : 'Download Data'}
                                        {!downloadLoading && (
                                            <img
                                                src={`${process.env.PUBLIC_URL}/images/desktop/material-download.svg`}
                                                alt="Download Icon"
                                                className="w-4 h-4"
                                            />
                                        )}
                                    </button>
                                ) : null}
                                <button
                                    onClick={() => setIsFilterOpen(!isFilterOpen)}
                                    className="text-gray-600 hover:text-gray-800"
                                >
                                    <img
                                        src={`${process.env.PUBLIC_URL}/images/desktop/NavFilter.svg`}
                                        alt="filters-icon"
                                        className="w-[35px] h-[35px]"
                                    />
                                </button>
                            </div>

                            {isFilterOpen && (
                                <div
                                    ref={filterPopupRef}
                                    className="absolute top-0 right-0 transform translate-x-3 w-[261px] h-[330px] bg-white border rounded-3xl shadow-lg z-50 flex flex-col"
                                >
                                    <div className="flex justify-between items-center p-4">
                                        <h3 className="text-[14px] font-normal leading-[20px] text-left">Filters</h3>
                                        <img
                                            src={`${process.env.PUBLIC_URL}/images/desktop/black-filter.svg`}
                                            alt="filters-black-icon"
                                            onClick={() => setIsFilterOpen(false)}
                                            className="w-9 h-9"
                                        />
                                    </div>

                                    <div className="flex-1 overflow-y-auto px-4" style={{ maxHeight: "calc(100% - 120px)" }}>
                                        <div className="space-y-4">
                                            {/* Source Filters */}
                                            <div>
                                                <span className="text-[12px] font-normal leading-[16px]">Source:</span>
                                                <div className="mt-2 space-y-2">
                                                    {filterOptions.sources.map(source => (
                                                        <label key={source.id} className="flex items-center space-x-2 cursor-pointer">
                                                            <div className="relative">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={filters.source.includes(source.id)}
                                                                    onChange={() => handleFilterChange('source', source.id)}
                                                                    className="peer appearance-none h-5 w-5 border-2 border-gray-300 rounded-sm cursor-pointer checked:border-orange-500 checked:bg-white hover:border-orange-500 transition-colors"
                                                                />
                                                                <div className="absolute top-1/2 left-1/2 transform -translate-x-1 -translate-y-1.5 opacity-0 peer-checked:opacity-100 pointer-events-none z-10">
                                                                    <div className="w-2 h-2 rounded-sm bg-orange-500"></div>
                                                                </div>
                                                            </div>
                                                            <span className="text-[12px] font-normal">{source.source}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Stage Filters */}
                                            <div>
                                                <span className="text-[12px] font-normal leading-[16px]">Stage:</span>
                                                <div className="mt-2 space-y-2">
                                                    {filterOptions.stages.map(stage => (
                                                        <label key={stage.id} className="flex items-center space-x-2 cursor-pointer">
                                                            <div className="relative">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={filters.stage.includes(stage.id)}
                                                                    onChange={() => handleFilterChange('stage', stage.id)}
                                                                    className="peer appearance-none h-5 w-5 border-2 border-gray-300 rounded-sm cursor-pointer checked:border-orange-500 checked:bg-white hover:border-orange-500 transition-colors"
                                                                />
                                                                <div className="absolute top-1/2 left-1/2 transform -translate-x-1 -translate-y-1.5 opacity-0 peer-checked:opacity-100 pointer-events-none z-10">
                                                                    <div className="w-2 h-2 rounded-sm bg-orange-500"></div>
                                                                </div>
                                                            </div>
                                                            <span className="text-[12px] font-normal">{stage.stage}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Priority Filters */}
                                            <div>
                                                <span className="text-[12px] font-normal leading-[16px]">Priority:</span>
                                                <div className="mt-2 space-y-2">
                                                    {filterOptions.priorities.map(priority => (
                                                        <label key={priority.id} className="flex items-center space-x-2 cursor-pointer">
                                                            <div className="relative">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={filters.priority.includes(priority.id)}
                                                                    onChange={() => handleFilterChange('priority', priority.id)}
                                                                    className="peer appearance-none h-5 w-5 border-2 border-gray-300 rounded-sm cursor-pointer checked:border-orange-500 checked:bg-white hover:border-orange-500 transition-colors"
                                                                />
                                                                <div className="absolute top-1/2 left-1/2 transform -translate-x-1 -translate-y-1.5 opacity-0 peer-checked:opacity-100 pointer-events-none z-10">
                                                                    <div className="w-2 h-2 rounded-sm bg-orange-500"></div>
                                                                </div>
                                                            </div>
                                                            <span className="text-[12px] font-normal">{priority.status}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Admission Status Filters */}
                                            <div>
                                                <span className="text-[12px] font-normal leading-[16px]">Admission Status:</span>
                                                <div className="mt-2 space-y-2">
                                                    {filterOptions.admissionStatuses.map(status => (
                                                        <label key={status.id} className="flex items-center space-x-2 cursor-pointer">
                                                            <div className="relative">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={filters.admissionStatus.includes(status.id)}
                                                                    onChange={() => handleFilterChange('admissionStatus', status.id)}
                                                                    className="peer appearance-none h-5 w-5 border-2 border-gray-300 rounded-sm cursor-pointer checked:border-orange-500 checked:bg-white hover:border-orange-500 transition-colors"
                                                                />
                                                                <div className="absolute top-1/2 left-1/2 transform -translate-x-1 -translate-y-1.5 opacity-0 peer-checked:opacity-100 pointer-events-none z-10">
                                                                    <div className="w-2 h-2 rounded-sm bg-orange-500"></div>
                                                                </div>
                                                            </div>
                                                            <span className="text-[12px] font-normal">{status.admissionStatus}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="p-4 border-t">
                                        <div className="flex justify-between space-x-3">
                                            <button
                                                onClick={() => {
                                                    setIsFilterOpen(false);
                                                    setCollaborators([]);
                                                    setSelectedUserDetails([]);
                                                    setSelectedStudent(null);
                                                    setSearchTerm('');
                                                    setSearchResults([]);
                                                }}
                                                className="px-8 py-2 text-white bg-black rounded-full"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                onClick={applyFilters}
                                                className="px-8 py-2 text-white bg-cyan-600 rounded-full"
                                            >
                                                Apply
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Students Table */}
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead>
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created On</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stage</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Admission Status</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {students.map((student) => (
                                    <tr key={student.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">{student.name}</td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {student.createdAt.toDate().toLocaleDateString()}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {student.source.source}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span
                                                className="px-2 py-1 rounded-full text-xs"
                                                style={{ backgroundColor: student.stage.color + '20', color: student.stage.color }}
                                            >
                                                {student.stage.stage}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span
                                                className="px-2 py-1 rounded-full text-xs"
                                                style={{ backgroundColor: student.priority.color + '20', color: student.priority.color }}
                                            >
                                                {student.priority.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {student.admissionStatus?.admissionStatus || 'Not Set'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <img
                                                    src={`${process.env.PUBLIC_URL}/images/desktop/print-preview.svg`}
                                                    alt="preview-icon"
                                                    className="w-7 h-7 ml-2 cursor-pointer"
                                                    onClick={() => handleStudentClick(student.id)}
                                                />
                                                <img
                                                    src={`${process.env.PUBLIC_URL}/images/desktop/collaborate.svg`}
                                                    alt="collaborate-icon"
                                                    className="w-7 h-7 ml-2 cursor-pointer"
                                                    onClick={() => handleCollaborateClick(student)}
                                                />
                                                <img
                                                    src={`${process.env.PUBLIC_URL}/images/desktop/TransferLead.svg`}
                                                    alt="transfer-lead-icon"
                                                    className="w-7 h-7 ml-2 cursor-pointer"
                                                    onClick={() => handleTransferClick(student)}
                                                />
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Load More Button */}
                    {hasMore && (
                        <div className="mt-4 text-center">
                            <button
                                onClick={loadMore}
                                disabled={loading}
                                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-400"
                            >
                                {loading ? 'Loading...' : 'Load More'}
                            </button>
                        </div>
                    )}
                </div>
            </div>
            {showCollaborateModal && selectedStudent && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-96">
                        <h2 className="text-lg font-semibold mb-4">Add Collaborators</h2>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700">Student Name</label>
                            <div className="mt-1 p-2 bg-gray-50 rounded-md">
                                {selectedStudent.name}
                            </div>
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700">Phone Number</label>
                            <div className="mt-1 p-2 bg-gray-50 rounded-md">
                                {selectedStudentPhone}
                            </div>
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700">Select Collaborators</label>
                            <div className="relative mt-1">
                                <input
                                    type="text"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-cyan-500 focus:border-cyan-500"
                                    placeholder="Type 3 or more characters to search users..."
                                    value={searchTerm}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setSearchTerm(value);
                                        handleSearchUsers(value);
                                    }}
                                />
                                {isLoadingUsers && (
                                    <div className="absolute right-2 top-2">
                                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-cyan-600"></div>
                                    </div>
                                )}

                                {/* Dropdown of search results */}
                                {searchTerm.length >= 3 && searchResults.length > 0 && (
                                    <div className="absolute z-10 w-full mt-1 bg-white shadow-lg max-h-60 rounded-md py-1 text-base overflow-auto focus:outline-none sm:text-sm">
                                        {searchResults
                                            .filter(user => !collaborators.includes(user.id))
                                            .map(user => (
                                                <div
                                                    key={user.id}
                                                    className="cursor-pointer hover:bg-gray-100 px-4 py-2"
                                                    onClick={() => {
                                                        handleSelectUser(user.id);
                                                        setSearchTerm('');
                                                    }}
                                                >
                                                    {user.name}
                                                </div>
                                            ))}
                                    </div>
                                )}
                            </div>

                            {/* Selected users display */}
                            <div className="mt-2 flex flex-wrap gap-2">
                                {selectedUserDetails.map(user => (
                                    <div
                                        key={user.id}
                                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800"
                                    >
                                        {user.name}
                                        <button
                                            type="button"
                                            className="flex-shrink-0 ml-1 h-4 w-4 rounded-full inline-flex items-center justify-center text-cyan-600 hover:bg-cyan-200 hover:text-cyan-500 focus:outline-none"
                                            onClick={() => handleRemoveUser(user.id)}
                                        >
                                            <span className="sr-only">Remove {user.name}</span>
                                            <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => {
                                    setShowCollaborateModal(false);
                                    setCollaborators([]);
                                    setSelectedUserDetails([]);
                                    setSelectedStudent(null);
                                    setSearchTerm('');
                                    setSearchResults([]);
                                }}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleCollaborateSubmit}
                                className="px-4 py-2 text-sm font-medium text-white bg-cyan-600 rounded-md hover:bg-cyan-700"
                            >
                                Submit
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {showTransferModal && selectedStudent && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-3xl w-[500px] max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-xl font-medium">Transfer Lead</h3>
                                <button onClick={() => setShowTransferModal(false)} className="text-gray-500 hover:text-gray-700">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            <div className="mb-6">
                                <p className="text-sm text-gray-600 mb-1">Student Name</p>
                                <p className="font-medium">{selectedStudent.name}</p>
                                <p className="text-sm text-gray-600 mt-3 mb-1">Phone Number</p>
                                <p className="font-medium">{selectedStudentPhone}</p>
                                {isAdmin && currentScreen === "Admin" && (
                                    <>
                                        <p className="text-sm text-gray-600 mt-3 mb-1">Current Relationship Manager</p>
                                        <p className="font-medium">{currentAssignedUser?.name || 'Not assigned'}</p>
                                    </>
                                )}
                            </div>

                            <div className="mb-6">
                                <label className="block text-sm text-gray-600 mb-2">Search Users</label>
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        handleSearchUsers(e.target.value);
                                    }}
                                    placeholder="Type to search users..."
                                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                                />
                            </div>

                            {isLoadingUsers ? (
                                <div className="text-center py-4">Loading...</div>
                            ) : (
                                <div className="max-h-48 overflow-y-auto mb-6">
                                    {searchResults.map(user => (
                                        <div
                                            key={user.id}
                                            onClick={() => handleSelectTransferUser(user)}
                                            className={`p-3 rounded-lg cursor-pointer mb-2 ${selectedTransferUser?.id === user.id
                                                ? 'bg-cyan-100 border-2 border-cyan-500'
                                                : 'hover:bg-gray-100'
                                                }`}
                                        >
                                            {user.name}
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="flex justify-end space-x-3">
                                <button
                                    onClick={() => setShowTransferModal(false)}
                                    className="px-6 py-2 text-gray-700 border border-gray-300 rounded-full hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleTransferSubmit}
                                    disabled={!selectedTransferUser}
                                    className={`px-6 py-2 text-white rounded-full ${selectedTransferUser
                                        ? 'bg-cyan-600 hover:bg-cyan-700'
                                        : 'bg-gray-400 cursor-not-allowed'
                                        }`}
                                >
                                    Transfer
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {showToast && (
                <Toast
                    message={toastMessage}
                    isError={
                        toastMessage.includes('Error') ||
                        toastMessage.includes('Cannot') ||
                        toastMessage.includes('Failed')
                    }
                    show={showToast}
                    onClose={() => setShowToast(false)}
                />
            )}
        </div>
    );
};

export default AllStudents;